"use client"

import { apiRequest, getJWTToken } from "@/lib/api"

// ---- Types -----------------------------------------------------------------

export type OrgRole = "owner" | "admin" | "recruiter" | "hiring_manager" | "viewer"

export interface OrgSummary {
  org_id: string
  role: OrgRole
  slug: string
  name: string
  logo_url?: string | null
  plan: string
}

export interface PlanSpec {
  label: string
  price_eur_month: number
  max_active_jobs: number
  max_seats: number
  pool_reveals_month: number
  ai_interviews_month: number
}

export interface OrgDetail {
  id: string
  slug: string
  name: string
  domain?: string | null
  logo_url?: string | null
  industry?: string | null
  size_band?: string | null
  locations: unknown[]
  profile: Record<string, unknown>
  verified: boolean
  plan: string
  plan_caps: {
    label: string
    price_eur_month: number
    max_active_jobs: number
    max_seats: number
    pool_reveals_month: number
    ai_interviews_month: number
  }
  member_count?: number
  my_role?: OrgRole
  created_at?: string
}

export interface OrgMemberRow {
  user_id: string
  role: OrgRole
  status: "active" | "suspended"
  name: string
  email?: string | null
  joined_at: string
}

export interface PendingInvite {
  id: string
  email: string
  role: OrgRole
  status: string
  created_at: string
  expires_at: string
}

// ---- Active-org persistence --------------------------------------------------

const ACTIVE_ORG_KEY = "b2b_active_org"
export const PENDING_INVITE_KEY = "b2b_pending_invite_token"

export function getActiveOrgId(): string | null {
  if (typeof window === "undefined") return null
  return localStorage.getItem(ACTIVE_ORG_KEY)
}

export function setActiveOrgId(orgId: string | null) {
  if (typeof window === "undefined") return
  if (orgId) localStorage.setItem(ACTIVE_ORG_KEY, orgId)
  else localStorage.removeItem(ACTIVE_ORG_KEY)
}

// ---- Org-scoped request helper -----------------------------------------------

// Every b2b endpoint is authenticated (get_current_user / require_org_role), so
// each request MUST carry the caller's Bearer JWT. The shared apiRequest sends
// only the empty proxy key by default; when that reaches the /api/ext proxy with
// no "Bearer " scheme the service key is injected and the backend's HTTPBearer
// guard rejects it with 403 "Not authenticated". Attaching the JWT here is what
// makes the whole employer surface authenticate.
function b2bAuthHeaders(orgId?: string): Record<string, string> {
  const headers: Record<string, string> = {}
  const token = getJWTToken()
  if (token) headers["Authorization"] = `Bearer ${token}`
  const id = orgId ?? getActiveOrgId()
  if (id) headers["X-Org-Id"] = id
  return headers
}

export function b2bRequest<T>(
  endpoint: string,
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE" = "GET",
  body?: unknown,
  orgId?: string,
): Promise<T> {
  return apiRequest<T>(`/b2b${endpoint}`, method, body, b2bAuthHeaders(orgId))
}

// ---- API surface ---------------------------------------------------------------

export const b2bApi = {
  myOrgs: () => b2bRequest<{ organizations: OrgSummary[] }>("/orgs/mine"),
  createOrg: (input: { name: string; website?: string; industry?: string; size_band?: string }) =>
    b2bRequest<OrgDetail>("/orgs", "POST", input),
  currentOrg: (orgId?: string) => b2bRequest<OrgDetail>("/orgs/current", "GET", undefined, orgId),
  updateOrg: (updates: Partial<Pick<OrgDetail, "name" | "logo_url" | "industry" | "size_band" | "domain">> & {
    locations?: unknown[]
    profile?: Record<string, unknown>
  }) => b2bRequest<OrgDetail>("/orgs/current", "PATCH", updates),
  scrape: (url?: string) => b2bRequest<OrgDetail>("/orgs/current/scrape", "POST", { url }),
  plans: () => b2bRequest<{ current: string; plans: Record<string, PlanSpec> }>("/billing/plans"),
  checkout: (plan: string) =>
    b2bRequest<{ checkout_url: string }>(`/billing/checkout/${plan}`, "POST"),
  members: () =>
    b2bRequest<{ members: OrgMemberRow[]; pending_invites: PendingInvite[] }>("/orgs/current/members"),
  invite: (email: string, role: OrgRole) =>
    b2bRequest<{ ok: boolean; email_sent?: boolean }>("/orgs/current/members/invite", "POST", { email, role }),
  revokeInvite: (inviteId: string) =>
    b2bRequest<{ ok: boolean }>(`/orgs/current/invites/${inviteId}`, "DELETE"),
  updateMember: (userId: string, updates: { role?: OrgRole; status?: "active" | "suspended" }) =>
    b2bRequest<{ ok: boolean }>(`/orgs/current/members/${encodeURIComponent(userId)}`, "PATCH", updates),
  removeMember: (userId: string) =>
    b2bRequest<{ ok: boolean }>(`/orgs/current/members/${encodeURIComponent(userId)}`, "DELETE"),
  invitePreview: (token: string) =>
    apiRequest<{ org_name: string; logo_url?: string | null; role: OrgRole; valid: boolean }>(
      `/b2b/invites/${encodeURIComponent(token)}`, "GET"),
  acceptInvite: (token: string) =>
    apiRequest<{ ok: boolean; org_id: string; role: OrgRole }>(
      "/b2b/invites/accept", "POST", { token }, b2bAuthHeaders()),
  // Pre-create autofill: scrape a company website (no org yet) and return a
  // suggested profile the onboarding form prefills. Never persists.
  scrapePreview: (url: string) =>
    apiRequest<OrgProfilePreview>("/b2b/orgs/scrape-preview", "POST", { url }, b2bAuthHeaders()),
}

export interface OrgProfilePreview {
  ok: boolean
  domain?: string | null
  name?: string | null
  industry?: string | null
  size_band?: string | null
  description?: string | null
  locations?: string[]
  benefits?: string[]
  logo_url?: string | null
}

// ---- Jobs ---------------------------------------------------------------------

export type JobStatus = "draft" | "published" | "paused" | "closed" | "archived"

export interface ScreeningQuestion {
  question: string
  type: "yes_no" | "text" | "number"
  knockout: boolean
  expected?: string | null
}

export interface ComplianceViolation {
  code: string
  severity: "block" | "warn"
  match: string
  message_de: string
  suggestion_de: string
}

export interface ComplianceReport {
  ok: boolean
  blocking: number
  violations: ComplianceViolation[]
}

export interface JobSummary {
  id: string
  slug: string
  status: JobStatus
  title: string
  employment_type?: string | null
  locations: unknown[]
  remote_pct?: number | null
  published_at?: string | null
  closes_at?: string | null
  created_at: string
  updated_at: string
  compliance_report?: ComplianceReport | null
}

export interface JobDetail extends JobSummary {
  description_html: string
  skills: string[]
  seniority?: string | null
  salary_min?: number | null
  salary_max?: number | null
  salary_currency: string
  salary_period: "year" | "month" | "hour"
  language_requirements: { language: string; level: string }[]
  visa_sponsorship: boolean
  screening_questions: ScreeningQuestion[]
  version: number
}

export const jobsApi = {
  list: (status?: JobStatus) =>
    b2bRequest<{ jobs: JobSummary[]; active_count: number; max_active_jobs: number }>(
      `/jobs${status ? `?status=${status}` : ""}`),
  generate: (intent: string, language: "de" | "en" = "de") =>
    b2bRequest<JobDetail>("/jobs/generate", "POST", { intent, language }),
  create: (title: string, description_html = "") =>
    b2bRequest<JobDetail>("/jobs", "POST", { title, description_html }),
  get: (jobId: string) => b2bRequest<JobDetail>(`/jobs/${jobId}`),
  update: (jobId: string, updates: Partial<JobDetail>) =>
    b2bRequest<JobDetail>(`/jobs/${jobId}`, "PATCH", updates),
  lint: (jobId: string) => b2bRequest<ComplianceReport>(`/jobs/${jobId}/lint`, "POST"),
  setStatus: (jobId: string, status: JobStatus) =>
    b2bRequest<JobDetail>(`/jobs/${jobId}/status`, "POST", { status }),
}

// ---- Applicants ------------------------------------------------------------------

export type ApplicantStage =
  | "new" | "screened" | "shortlist" | "interview" | "offer" | "hired" | "rejected"

export const APPLICANT_STAGES: ApplicantStage[] = [
  "new", "screened", "shortlist", "interview", "offer", "hired", "rejected",
]

export interface ApplicantSummary {
  id: string
  first_name: string
  last_name: string
  email: string
  stage: ApplicantStage
  source: "native" | "form" | "email" | "pool_invite"
  knockout_flagged: boolean
  cv_file?: string | null
  created_at: string
  updated_at: string
  anonymized_at?: string | null
}

export interface ApplicantNote {
  id: string
  author_id: string
  body: string
  created_at: string
}

export interface ApplicantDetail extends ApplicantSummary {
  phone?: string | null
  profile: Record<string, unknown>
  screening_answers: { question: string; answer: string }[]
  stage_history: { from: string; to: string; by: string; at: string; reason?: string }[]
  notes: ApplicantNote[]
  job?: { id: string; title: string; slug: string } | null
}

export interface OrgAnalytics {
  totals: { applicants: number; hired: number; rejected: number; in_pipeline: number }
  jobs: {
    job_id: string; title: string; status: JobStatus; total: number
    by_stage: Record<string, number>; by_source: Record<string, number>
  }[]
}

export const applicantsApi = {
  analytics: () => b2bRequest<OrgAnalytics>("/orgs/current/analytics"),
  list: (jobId: string) =>
    b2bRequest<{ applicants: ApplicantSummary[]; counts: Record<string, number>; stages: ApplicantStage[] }>(
      `/jobs/${jobId}/applicants`),
  get: (applicantId: string) => b2bRequest<ApplicantDetail>(`/applicants/${applicantId}`),
  setStage: (applicantId: string, stage: ApplicantStage, opts?: { reason?: string; suppress_email?: boolean }) =>
    b2bRequest<{ ok: boolean; stage: ApplicantStage }>(
      `/applicants/${applicantId}/stage`, "PATCH", { stage, ...opts }),
  addNote: (applicantId: string, body: string) =>
    b2bRequest<ApplicantNote>(`/applicants/${applicantId}/notes`, "POST", { body }),
  messages: (applicantId: string) =>
    b2bRequest<{ messages: ApplicantMessage[] }>(`/applicants/${applicantId}/messages`),
  sendMessage: (applicantId: string, subject: string, body: string) =>
    b2bRequest<ApplicantMessage>(`/applicants/${applicantId}/messages`, "POST", { subject, body }),
}

export interface ApplicantMessage {
  id: string
  direction: "out" | "in"
  subject?: string | null
  body: string
  sent_by?: string | null
  created_at: string
}

// ---- Public (no auth) --------------------------------------------------------------

export interface PublicJob {
  slug: string
  title: string
  description_html: string
  skills: string[]
  employment_type?: string | null
  salary_min?: number | null
  salary_max?: number | null
  salary_currency: string
  salary_period: string
  locations: unknown[]
  remote_pct?: number | null
  screening_questions: { question: string; type: "yes_no" | "text" | "number" }[]
  company: {
    slug: string; name: string; logo_url?: string | null
    industry?: string | null; description?: string | null; verified: boolean
  }
}

export interface PublicOrg {
  slug: string
  name: string
  logo_url?: string | null
  industry?: string | null
  size_band?: string | null
  description?: string | null
  benefits?: unknown
  verified: boolean
  jobs: { slug: string; title: string; employment_type?: string | null; locations: unknown[]; remote_pct?: number | null }[]
}

export const publicApi = {
  org: (slug: string) => apiRequest<PublicOrg>(`/b2b/public/orgs/${encodeURIComponent(slug)}`, "GET"),
  job: (slug: string) => apiRequest<PublicJob>(`/b2b/public/jobs/${encodeURIComponent(slug)}`, "GET"),
  apply: (slug: string, form: FormData) =>
    apiRequest<{ ok: boolean; application_id?: string }>(
      `/b2b/public/jobs/${encodeURIComponent(slug)}/apply`, "POST", form, {}, true),
}

// ---- Phase 2: sourcing / screening / scheduling / copilot -----------------------

export interface CandidateCard {
  handle: string
  revealed: boolean
  candidate_user_id?: string
  score: number
  matched_skills: string[]
  location: string
  availability: string
  skills_count: number
}

export interface RevealedCandidate {
  candidate_user_id: string
  name: string
  email?: string | null
  skills: string[]
  cv_excerpt: string
}

export interface ScreeningSummary {
  summary: string
  strengths: string[]
  gaps: string[]
  flags: string[]
  recommendation: "promising" | "neutral" | "weak"
}

export interface ScreeningRecord {
  id: string
  status: "invited" | "in_progress" | "completed" | "expired" | "opted_out"
  summary?: ScreeningSummary | null
  transcript: { question: string; answer?: string }[]
  human_decision?: "advance" | "reject" | "undecided" | null
  created_at: string
  completed_at?: string | null
}

export interface InterviewSlot {
  id: string
  job_id?: string | null
  starts_at: string
  ends_at: string
  location: string
  status: "open" | "booked" | "cancelled"
  booked_applicant_id?: string | null
}

export interface DigestItem {
  kind: string
  severity: "high" | "medium" | "low"
  count: number
  text_de: string
  applicant_ids?: string[]
  job_titles?: string[]
}

export const sourcingApi = {
  candidates: (jobId: string) =>
    b2bRequest<{ candidates: CandidateCard[]; reveals_used_month: number; reveals_cap: number }>(
      `/jobs/${jobId}/candidates`),
  reveal: (jobId: string, handle: string) =>
    b2bRequest<RevealedCandidate>(`/jobs/${jobId}/candidates/${handle}/reveal`, "POST"),
  invite: (candidateUserId: string, jobId: string, message?: string) =>
    b2bRequest<{ ok: boolean }>(`/candidates/${encodeURIComponent(candidateUserId)}/invite`,
      "POST", { job_id: jobId, message }),
}

export const screeningApi = {
  invite: (applicantId: string) =>
    b2bRequest<{ ok: boolean }>(`/applicants/${applicantId}/screening`, "POST"),
  get: (applicantId: string) =>
    b2bRequest<{ screenings: ScreeningRecord[] }>(`/applicants/${applicantId}/screening`),
  decide: (screeningId: string, decision: "advance" | "reject" | "undecided") =>
    b2bRequest<{ ok: boolean }>(`/screenings/${screeningId}/decision`, "PATCH", { decision }),
  // public candidate side
  state: (token: string) =>
    apiRequest<{ status: string; org_name: string; job_title: string; disclosure: string
      question_number: number; max_questions: number; current_question?: string | null }>(
      `/b2b/public/screening/${token}`, "GET"),
  start: (token: string) =>
    apiRequest<{ question: string; question_number: number; max_questions: number }>(
      `/b2b/public/screening/${token}/start`, "POST"),
  answer: (token: string, answer: string) =>
    apiRequest<{ done?: boolean; question?: string; question_number?: number; max_questions?: number }>(
      `/b2b/public/screening/${token}/answer`, "POST", { answer }),
  optout: (token: string) =>
    apiRequest<{ ok: boolean; message: string }>(`/b2b/public/screening/${token}/optout`, "POST"),
}

export const schedulingApi = {
  slots: () => b2bRequest<{ slots: InterviewSlot[] }>("/orgs/current/slots"),
  createSlot: (input: { starts_at: string; ends_at: string; location?: string; job_id?: string }) =>
    b2bRequest<InterviewSlot>("/orgs/current/slots", "POST", input),
  cancelSlot: (slotId: string) =>
    b2bRequest<{ ok: boolean }>(`/orgs/current/slots/${slotId}`, "DELETE"),
  sendInvite: (applicantId: string) =>
    b2bRequest<{ ok: boolean }>(`/applicants/${applicantId}/schedule_invite`, "POST"),
  // public
  state: (token: string) =>
    apiRequest<{ status: string; org_name: string; job_title: string
      slots?: InterviewSlot[]; booked_slot?: InterviewSlot | null }>(
      `/b2b/public/schedule/${token}`, "GET"),
  book: (token: string, slotId: string) =>
    apiRequest<{ ok: boolean; starts_at: string; location: string; calendar_url: string }>(
      `/b2b/public/schedule/${token}/book`, "POST", { slot_id: slotId }),
}

export const copilotApi = {
  digest: () => b2bRequest<{ items: DigestItem[]; generated_at: string }>("/copilot/digest"),
  draftMessage: (applicantId: string, instruction: string) =>
    b2bRequest<{ subject: string; body: string }>("/copilot/draft_message", "POST",
      { applicant_id: applicantId, instruction }),
}

// ---- P1-14 scorecards / P2-13 question bank / P3-4 folders / P3-7 autopilot / P3-9 API ----

export interface ScorecardCriterion {
  name: string
  rating: number
  comment?: string
}

export interface Scorecard {
  id: string
  author_id: string
  criteria: ScorecardCriterion[]
  overall?: number | null
  recommendation?: "strong_yes" | "yes" | "neutral" | "no" | "strong_no" | null
  comment?: string | null
  created_at: string
  updated_at: string
}

export const scorecardApi = {
  list: (applicantId: string) =>
    b2bRequest<{ scorecards: Scorecard[]; average_overall: number | null }>(
      `/applicants/${applicantId}/scorecards`),
  upsert: (applicantId: string, input: {
    criteria?: ScorecardCriterion[]
    overall?: number
    recommendation?: string
    comment?: string
  }) => b2bRequest<Scorecard>(`/applicants/${applicantId}/scorecard`, "PUT", input),
}

export interface BankQuestion {
  id: string
  job_id?: string | null
  question: string
  position: number
  created_at: string
}

export const questionBankApi = {
  list: (jobId?: string) =>
    b2bRequest<{ questions: BankQuestion[] }>(
      `/orgs/current/question_bank${jobId ? `?job_id=${jobId}` : ""}`),
  add: (question: string, jobId?: string, position = 0) =>
    b2bRequest<BankQuestion>("/orgs/current/question_bank", "POST",
      { question, job_id: jobId ?? null, position }),
  remove: (questionId: string) =>
    b2bRequest<{ ok: boolean }>(`/orgs/current/question_bank/${questionId}`, "DELETE"),
}

export interface PoolFolder {
  id: string
  name: string
  member_count?: number
  created_at: string
}

export const folderApi = {
  list: () => b2bRequest<{ folders: PoolFolder[] }>("/orgs/current/folders"),
  create: (name: string) =>
    b2bRequest<PoolFolder>("/orgs/current/folders", "POST", { name }),
  remove: (folderId: string) =>
    b2bRequest<{ ok: boolean }>(`/orgs/current/folders/${folderId}`, "DELETE"),
  addMember: (folderId: string, candidateUserId: string, note?: string) =>
    b2bRequest<{ ok: boolean }>(`/orgs/current/folders/${folderId}/members`, "POST",
      { candidate_user_id: candidateUserId, note }),
  members: (folderId: string) =>
    b2bRequest<{ folder: { id: string; name: string }; members: {
      candidate_user_id: string; note?: string | null; added_at: string; consent_active: boolean
    }[] }>(`/orgs/current/folders/${folderId}/members`),
  removeMember: (folderId: string, candidateUserId: string) =>
    b2bRequest<{ ok: boolean }>(
      `/orgs/current/folders/${folderId}/members/${encodeURIComponent(candidateUserId)}`, "DELETE"),
}

export type AutopilotSettings = Record<string, string>

export const autopilotApi = {
  get: () => b2bRequest<{ settings: AutopilotSettings; steps: Record<string, string[]> }>(
    "/copilot/autopilot"),
  // Explicit-toggle-only writes — never call from an autosave path.
  set: (settings: AutopilotSettings) =>
    b2bRequest<{ settings: AutopilotSettings }>("/copilot/autopilot", "PATCH", { settings }),
}

export interface ApiKeyInfo {
  id: string
  prefix: string
  label?: string | null
  created_at: string
  last_used_at?: string | null
  revoked_at?: string | null
}

export interface WebhookInfo {
  id: string
  url: string
  events: string[]
  active: boolean
  created_at: string
  last_status?: number | null
  last_delivery_at?: string | null
  failure_count: number
}

export const integrationApi = {
  keys: () => b2bRequest<{ keys: ApiKeyInfo[] }>("/orgs/current/api_keys"),
  createKey: (label?: string) =>
    b2bRequest<{ id: string; key: string; prefix: string; note: string }>(
      "/orgs/current/api_keys", "POST", { label }),
  revokeKey: (keyId: string) =>
    b2bRequest<{ ok: boolean }>(`/orgs/current/api_keys/${keyId}`, "DELETE"),
  webhooks: () =>
    b2bRequest<{ webhooks: WebhookInfo[]; supported_events: string[] }>("/orgs/current/webhooks"),
  createWebhook: (url: string, events: string[]) =>
    b2bRequest<{ id: string; url: string; events: string[]; secret: string; note: string }>(
      "/orgs/current/webhooks", "POST", { url, events }),
  removeWebhook: (webhookId: string) =>
    b2bRequest<{ ok: boolean }>(`/orgs/current/webhooks/${webhookId}`, "DELETE"),
}

export const ROLE_LABEL_KEYS: Record<OrgRole, string> = {
  owner: "roles.owner",
  admin: "roles.admin",
  recruiter: "roles.recruiter",
  hiring_manager: "roles.hiring_manager",
  viewer: "roles.viewer",
}

export function roleAtLeast(role: OrgRole | undefined, minimum: OrgRole): boolean {
  const order: OrgRole[] = ["viewer", "hiring_manager", "recruiter", "admin", "owner"]
  if (!role) return false
  return order.indexOf(role) >= order.indexOf(minimum)
}
